import React, { useState, useEffect } from 'react';
import { Clock, Facebook, Twitter, Instagram, Linkedin, Search, Menu, X, Phone } from 'lucide-react';
import { navigation, contactInfo, socialLinks } from '../data/mockData';
import CurrencySelector from './CurrencySelector';

const Header = ({ onOpenBuilder }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    if (href === '#builder') {
      onOpenBuilder();
    } else if (href === '#home' || href === '/') {
      // Scroll to top of page or reload if already at top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Clear any URL parameters
      if (window.location.search || window.location.pathname !== '/') {
        window.location.href = '/';
      }
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    // Navigate to home and refresh
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.location.pathname !== '/' || window.location.search) {
      window.location.href = '/';
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-lg' : ''}`}>
      {/* Top Bar */}
      <div className="bg-[#1a1a1a] py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Working Hours */}
          <div className="flex items-center gap-2 text-white text-sm">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">{contactInfo.workingHours}</span>
            <span className="hidden md:inline">| {contactInfo.closedDays}</span>
          </div>

          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={handleLogoClick}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && handleLogoClick()}
          >
            <div className="bg-white/90 rounded-lg p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_a00d389d-55ed-4ba2-b25d-abeefa2cf3c1/artifacts/kc0888c6_Art%20%26%20Illustration%20-%20Edited.png" 
                alt="ZamZam Umrah Logo" 
                className="h-12 w-auto"
              />
            </div>
            <div className="text-white font-bold leading-tight">
              <span className="text-[#d4af37] text-lg font-semibold tracking-wider" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>ZAMZAM</span>
              <br />
              <span className="text-sm tracking-[0.15em]">UMRAH</span>
            </div>
          </div>

          {/* Phone Number - Blinking + Currency */}
          <div className="flex items-center gap-4">
            <a 
              href="tel:01582800304" 
              className="flex items-center gap-2 text-white animate-pulse"
            >
              <Phone className="w-5 h-5 text-[#d4af37]" />
              <span className="font-bold text-lg blinking-phone">{contactInfo.phone}</span>
            </a>
            
            {/* Currency Selector */}
            <CurrencySelector variant="compact" />
            
            <div className="hidden md:flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  className="text-white hover:text-[#d4af37] transition-colors"
                  aria-label={social.name}
                >
                  {social.icon === 'facebook' && <Facebook className="w-4 h-4" />}
                  {social.icon === 'twitter' && <Twitter className="w-4 h-4" />}
                  {social.icon === 'instagram' && <Instagram className="w-4 h-4" />}
                  {social.icon === 'linkedin' && <Linkedin className="w-4 h-4" />}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="bg-[#d4af37]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-black"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`font-medium text-sm hover:text-white transition-colors tracking-wide ${
                    item.name === 'BUILD YOUR UMRAH' 
                      ? 'bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800' 
                      : 'text-black'
                  }`}
                >
                  {item.name}
                </a>
              ))}
            </nav>

            {/* Search Icon */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="text-black hover:text-white transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          {searchOpen && (
            <div className="pb-4">
              <input
                type="text"
                placeholder="Search Umrah packages..."
                className="w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          )}

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden pb-4">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`block py-2 font-medium text-sm transition-colors ${
                    item.name === 'BUILD YOUR UMRAH' 
                      ? 'bg-black text-white px-4 rounded-md my-2 text-center' 
                      : 'text-black hover:text-white'
                  }`}
                >
                  {item.name}
                </a>
              ))}
            </nav>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
